"use client";

import React, { useState } from "react";
import { useApp } from "./AppProvider";
import type { PricingPlan } from "@/lib/cms";

interface PricingPlansProps {
  plans: PricingPlan[];
  serviceSlug: string;
}

export default function PricingPlans({ plans, serviceSlug }: PricingPlansProps) {
  const { isRTL } = useApp();
  const [isAnnual, setIsAnnual] = useState(false);

  if (!plans || plans.length === 0) return null;

  return (
    <section
      id="pricing"
      style={{
        padding: "5rem 0",
        direction: isRTL ? "rtl" : "ltr",
        textAlign: isRTL ? "right" : "left",
      }}
    >
      {/* Section Header */}
      <div style={{ textAlign: "center", marginBottom: "3rem" }}>
        <div className="section-badge" style={{ marginInline: "auto" }}>
          💰 {isRTL ? "خطط الأسعار" : "Pricing Plans"}
        </div>
        <h2
          style={{
            fontSize: "clamp(2rem, 5vw, 3.2rem)",
            fontWeight: 800,
            color: "var(--text-primary)",
            letterSpacing: "-0.03em",
            marginBottom: "1rem",
          }}
        >
          {isRTL ? "اختر الخطة المناسبة لك" : "Choose the Right Plan"}
        </h2>
      </div>

      {/* Monthly / Annual Toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          marginBottom: "3rem",
        }}
      >
        <span
          style={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: !isAnnual ? "var(--text-primary)" : "var(--text-muted)",
            transition: "color 0.3s ease",
          }}
        >
          {isRTL ? "شهري" : "Monthly billing"}
        </span>

        {/* Toggle Pill */}
        <button
          onClick={() => setIsAnnual(!isAnnual)}
          aria-label="Toggle billing period"
          style={{
            width: 56,
            height: 30,
            borderRadius: 50,
            border: "none",
            background: "var(--gradient-brand)",
            cursor: "pointer",
            position: "relative",
            transition: "all 0.3s ease",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 3,
              left: isAnnual ? 29 : 3,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: "white",
              transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          />
        </button>

        <span
          style={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: isAnnual ? "var(--text-primary)" : "var(--text-muted)",
            transition: "color 0.3s ease",
          }}
        >
          {isRTL ? "سنوي" : "Annual billing"}
        </span>
      </div>

      {/* Plan Cards Grid */}
      <div
        className="pricing-grid"
        style={{
          gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`,
        }}
      >
        {plans.map((plan, i) => {
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          const isContact = !price || price === 0;
          const name = isRTL ? (plan.name_ar || plan.name_en) : plan.name_en;
          const desc = isRTL
            ? (plan.description_ar || plan.description_en)
            : plan.description_en;
          const ctaLabel = isRTL
            ? (plan.ctaLabel_ar || plan.ctaLabel_en)
            : plan.ctaLabel_en;
          const features = (plan.features || []);

          return (
            <div
              key={i}
              className="glass-card-strong"
              style={{
                borderRadius: 20,
                overflow: "hidden",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.35s ease, box-shadow 0.35s ease",
                ...(plan.isPopular
                  ? {
                      border: "2px solid var(--color-pink)",
                      boxShadow: "0 8px 40px rgba(227,1,125,0.18)",
                    }
                  : {}),
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(-6px)";
                (e.currentTarget as HTMLDivElement).style.boxShadow = plan.isPopular
                  ? "0 16px 60px rgba(227,1,125,0.25)"
                  : "0 12px 50px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                (e.currentTarget as HTMLDivElement).style.boxShadow = plan.isPopular
                  ? "0 8px 40px rgba(227,1,125,0.18)"
                  : "none";
              }}
            >
              {/* Popular Badge */}
              {plan.isPopular && (
                <div
                  style={{
                    background: "var(--gradient-brand)",
                    color: "white",
                    textAlign: "center",
                    padding: "0.5rem 0",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {isRTL ? "الأكثر شعبية" : "Most popular plan"}
                </div>
              )}

              {/* Card Content */}
              <div style={{ padding: "2.2rem 2rem", flex: 1, display: "flex", flexDirection: "column" }}>
                {/* Plan Name */}
                <h3
                  style={{
                    fontSize: "1.65rem",
                    fontWeight: 800,
                    color: "var(--text-primary)",
                    marginBottom: "0.5rem",
                  }}
                >
                  {name}
                </h3>

                {/* Description */}
                {desc && (
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "var(--text-secondary)",
                      lineHeight: 1.6,
                      marginBottom: "1.5rem",
                    }}
                  >
                    {desc}
                  </p>
                )}

                {/* Price */}
                <div style={{ marginBottom: "1.5rem" }}>
                  {isContact ? (
                    <span
                      className="gradient-text-brand"
                      style={{
                        fontSize: "1.8rem",
                        fontWeight: 800,
                      }}
                    >
                      {isRTL ? "تواصل معنا" : "Contact us"}
                    </span>
                  ) : (
                    <div style={{ display: "flex", alignItems: "baseline", gap: "0.3rem" }}>
                      <span
                        className="gradient-text-brand"
                        style={{
                          fontSize: "2.8rem",
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {plan.currency}{price}
                      </span>
                      <span
                        style={{
                          fontSize: "0.85rem",
                          color: "var(--text-muted)",
                          fontWeight: 500,
                        }}
                      >
                        {isRTL
                          ? isAnnual ? "/ سنوياً" : "/ شهرياً"
                          : isAnnual ? "billed annually" : "billed monthly"}
                      </span>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <a
                  href={plan.ctaLink || `/get-quote?ref=${serviceSlug}`}
                  className={plan.isPopular ? "btn-primary" : "btn-secondary"}
                  style={{
                    display: "inline-block",
                    textAlign: "center",
                    textDecoration: "none",
                    marginBottom: "2rem",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {ctaLabel || (isRTL ? "ابدأ الآن" : "Get Started")}
                </a>

                {/* Divider */}
                <div
                  style={{
                    height: 1,
                    background: "var(--border-subtle)",
                    marginBottom: "1.5rem",
                  }}
                />

                {/* Features Header */}
                {features.length > 0 && (
                  <div style={{ marginBottom: "1rem" }}>
                    <p
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: "var(--text-primary)",
                      }}
                    >
                      {i === 0
                        ? (isRTL ? "يشمل:" : "Includes:")
                        : isRTL
                          ? `كل ما في ${plans[i - 1] ? (isRTL ? plans[i - 1].name_ar || plans[i - 1].name_en : plans[i - 1].name_en) : ""} بالإضافة إلى:`
                          : `Everything in ${plans[i - 1]?.name_en || ""}, Plus:`}
                    </p>
                  </div>
                )}

                {/* Feature List */}
                <ul style={{ listStyle: "none", padding: 0, margin: 0, flex: 1 }}>
                  {features.map((f, fi) => (
                    <li
                      key={fi}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.65rem",
                        padding: "0.45rem 0",
                        fontSize: "0.88rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: "var(--badge-bg)",
                          flexShrink: 0,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            stroke="var(--color-pink)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      {isRTL ? (f.feature_ar || f.feature_en) : f.feature_en}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

    </section>
  );
}
